import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import CurrencyFormat from 'react-currency-format';
import { MdDeleteOutline, MdEdit, MdGroupWork, MdStars } from 'react-icons/md';
import { useParams } from 'react-router-dom';
import { Button, Checkbox, Table } from 'rsuite';
import type { TableColumnProps } from 'rsuite/lib/Table/TableColumn';
import { CheckBoxCell } from '../../../../../RevisaoPrecos/Components';
import { useCompetitorPriceStore } from '../../store/competitorPriceStore';
import { SortType, useOfferTable } from '../../useOfferTable';

import { updateOfferInlinePrice } from '../../services';
import CompetitorPriceCell from '../CompetitorPriceCell/CompetitorPriceCell';
import { PrecoVigenteCellCampanha } from '../PrecoVigenteCell';
import { useOfferInlineEditStore } from './hooks/useOfferInlineEditStore';
import { OfferCompetitorsExpandedRow } from './OfferCompetitorsExpandedRow';
import { OfferCpiCell } from './OfferCpiCell';
import { OfferDetailCell } from './OfferDetailCell';
import { OfferDiscountCell } from './OfferDiscountCell';
import OfferGroupCell from './OfferGroupCell';
import { OfferMarginCell } from './OfferMarginCell';
import { OfferPriceCell } from './OfferPriceCell';
import styles from './OfferTable.module.scss';
import { applyInlineOfferStateToRow } from '../../utils/applyInlineOfferStateToRow';

const Column = Table.Column as React.ComponentType<React.PropsWithChildren<TableColumnProps>>;
const HeaderCell = Table.HeaderCell;
const Cell = Table.Cell;
const Pagination = Table.Pagination;

const MemoCheckbox = memo(Checkbox);
const MemoCheckboxCell = memo(CheckBoxCell);
const MemoCompetitorPriceCell = memo(CompetitorPriceCell);

export interface IRowData {
  product_uuid: string;
  product_family_id?: string;
  is_product_master?: boolean;
  product_id: string;
  stores_id: string[];
  description: string;
  cost: number;
  margin: number;
  margin_price_retail?: number | null;
  pmz: number;
  retail_price: number;
  retail_price_cpi: number;
  avg_competitiveness_price: number;
  offer_price_cpi: number;
  offer_dto: OfferDto;
  offer_final_price: number;
  offer_discount_percentage: number;
  client_id: number;
  client_name: string;
  product_family?: string;
  offer_group: string;
  offer_group_id: string | null;
  sales_tax_percentage?: number | null;
  expenses_percent?: number | null;
  salesTaxPercentage?: number | null;
  expensesPercent?: number | null;
}

export interface OfferDto {
  offer: Offer;
  id: string;
}

export interface Offer {
  offer_type: string;
  offer_price?: number;
  percentage_discount?: number;
  from?: number;
  buy_quantity?: number;
  pay_quantity?: number;
  unit_buy?: string;
  minimum_quantity?: number;
  price_per_unit?: number;
  to?: number;
}

interface OfferTableProps {
  states: ReturnType<typeof useOfferTable>['states'];
  handlers: ReturnType<typeof useOfferTable>['handlers'];
  onEdit: (offer: IRowData) => void;
  onDelete: (offerId: string) => void;
  blockOptions: Array<{ label: string; value: string }>;
  campaignId: string;
  storeIds: string[];
  isReadOnly?: boolean;
}

export const OfferTable = ({ states, handlers, onEdit, onDelete, blockOptions, campaignId, storeIds, isReadOnly = false }: OfferTableProps) => {
  const { id } = useParams<{ id: string }>();
  const [tableKey, setTableKey] = useState(0);

  const { sort, offers, isCheckedAll, isIndeterminate, hasActiveFilters } = states;
  const { setSort, setPage, setSize, handleCheckAll, handleCheckItem, isChecked } = handlers;
  const tableRef = useRef<HTMLDivElement>(null);
  const expandedRowKey = useCompetitorPriceStore(state => state.key);
  const expandedRowOptions = useCompetitorPriceStore(state => state.options);
  const inlineRows = useOfferInlineEditStore(state => state.rows);

  const expandedRowKeys = useMemo(() => {
    if (!expandedRowKey) return [];
    if (!expandedRowOptions.includes('COMPETITORS')) return [];
    return [expandedRowKey];
  }, [expandedRowKey, expandedRowOptions]);

  const tableData = useMemo(() => {
    return (offers?.content ?? []).map((rowData: IRowData) => applyInlineOfferStateToRow(rowData, inlineRows?.[rowData.product_uuid]));
  }, [offers?.content, inlineRows]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTableKey(prevKey => prevKey + 1);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  const handleOfferGroupChange = (value: string, rowData: IRowData) => {
    if (isReadOnly) return;
    if (!id) return;
    const selectedOption = blockOptions.find(option => option.value === value);
    const store = useOfferInlineEditStore.getState();
    store.initRow(
      rowData.product_uuid,
      rowData.offer_final_price ?? null,
      rowData.offer_discount_percentage ?? null,
      rowData.offer_group_id ?? null,
      rowData.offer_group ?? null,
    );
    store.setOfferGroup(rowData.product_uuid, value, selectedOption?.label ?? null);
    const rowState = useOfferInlineEditStore.getState().rows[rowData.product_uuid];
    const payload = {
      product_id: rowData.product_uuid,
      new_offer_price: rowState?.originalPrice ?? rowData.offer_final_price,
      new_offer_discount: rowState?.originalDiscount ?? rowData.offer_discount_percentage,
      new_offer_group_id: value,
    };

    updateOfferInlinePrice(id, payload)
      .then(() => useOfferInlineEditStore.getState().commitOfferGroup(rowData.product_uuid))
      .catch(() => useOfferInlineEditStore.getState().rollbackOfferGroup(rowData.product_uuid));
  };

  if (!offers?.content?.length) {
    return (
      <section className={styles.container} data-empty>
        <div className={styles.empty}>{hasActiveFilters ? 'Nenhum dado encontrado' : 'Você ainda não criou nenhuma oferta'}</div>
      </section>
    );
  }

  return (
    <section className={styles.container} ref={tableRef}>
      <Table
        key={tableKey}
        className={styles.table}
        data={tableData}
        autoHeight
        minHeight={500}
        headerHeight={50}
        rowHeight={68}
        rowExpandedHeight={200}
        rowKey="product_uuid"
        expandedRowKeys={expandedRowKeys}
        renderRowExpanded={(rowData: IRowData) => <OfferCompetitorsExpandedRow rowData={rowData} campaignId={campaignId} storeIds={storeIds} />}
        sortColumn={sort.type}
        sortType={sort.orderBy}
        onSortColumn={(sortColumn, sortType) => {
          setSort({
            type: sortColumn as SortType,
            orderBy: sortType,
          });
        }}
      >
        <Column width={42}>
          <HeaderCell>
            <MemoCheckbox
              checked={isCheckedAll}
              indeterminate={isIndeterminate}
              onChange={(_, checked) => {
                handleCheckAll(checked);
              }}
            />
          </HeaderCell>
          <Cell dataKey="product_uuid" style={{ padding: 0 }}>
            {(rowData: IRowData) => {
              const value = rowData.product_uuid;
              return (
                <MemoCheckboxCell
                  value={value}
                  isChecked={isChecked(value as string)}
                  handleCheck={(value, checked) => {
                    handleCheckItem(checked, value as string);
                  }}
                />
              );
            }}
          </Cell>
        </Column>

        <Column sortable flexGrow={1} minWidth={200}>
          <HeaderCell>Produto</HeaderCell>
          <Cell dataKey="product_id">
            {(rowData: IRowData) => (
              <div className={styles['cell-product']}>
                <div className={styles['product-info']}>
                  <span className={styles['product-id']}>{rowData.product_id}</span>
                  {rowData.product_family_id ? (
                    <span className={styles['product-family']}>
                      <MdGroupWork />
                      {rowData.product_family_id}
                    </span>
                  ) : null}

                  {rowData.is_product_master ? (
                    <span className={styles['product-father']}>
                      <MdStars />
                      <span>PAI</span>
                    </span>
                  ) : null}
                </div>
                <div className={styles['product-description']}>{rowData.description}</div>
              </div>
            )}
          </Cell>
        </Column>

        <Column align="left" sortable width={104}>
          <HeaderCell className={styles['header-cell']}>
            <div>
              <p>Bloco de</p>
              <p>ofertas</p>
            </div>
          </HeaderCell>
          <Cell dataKey="block_name">
            {(rowData: IRowData) => (
              <OfferGroupCell rowData={rowData} blockOptions={blockOptions} onChange={handleOfferGroupChange} disabled={isReadOnly} />
            )}
          </Cell>
        </Column>

        <Column align="left" sortable width={164}>
          <HeaderCell>Detalhe da oferta</HeaderCell>
          <Cell dataKey="detail">
            {(rowData: IRowData) => (
              <div>
                <OfferDetailCell rowData={rowData} onEdit={onEdit} disabled={isReadOnly} />
              </div>
            )}
          </Cell>
        </Column>

        <Column align="left" sortable width={104}>
          <HeaderCell>PMZ</HeaderCell>
          <Cell className={styles['pmz']} dataKey="pmz">
            {(rowData: IRowData) => (
              <div>
                <CurrencyFormat
                  fixedDecimalScale
                  decimalScale={2}
                  value={rowData.pmz}
                  displayType="text"
                  prefix="R$ "
                  decimalSeparator=","
                  thousandSeparator="."
                />
              </div>
            )}
          </Cell>
        </Column>

        <Column align="left" sortable width={112}>
          <HeaderCell className={styles['header-cell']}>
            <div>
              <p>Preço</p>
              <p>regular</p>
            </div>
          </HeaderCell>
          <Cell dataKey="regular_price" className={styles['regular-price']}>
            {(rowData: IRowData) => <PrecoVigenteCellCampanha rowData={rowData} />}
          </Cell>
        </Column>

        <Column align="left" sortable width={104}>
          <HeaderCell className={styles['header-cell']}>
            <div>
              <p>Preço</p>
              <p>concorrência</p>
            </div>
          </HeaderCell>
          <Cell dataKey="price_competitors" className={styles['price-competitors']}>
            {(rowData: IRowData) => (
              <div>
                <MemoCompetitorPriceCell rowData={rowData} />
              </div>
            )}
          </Cell>
        </Column>

        <Column align="left" sortable width={124}>
          <HeaderCell className={styles['header-cell']}>Preço oferta</HeaderCell>
          <Cell dataKey="offer_final_price" className={styles['price-offer']}>
            {(rowData: IRowData) => <OfferPriceCell rowData={rowData} campaignId={id} disabled={isReadOnly} />}
          </Cell>
        </Column>

        <Column align="left" sortable width={124}>
          <HeaderCell>Desconto %</HeaderCell>
          <Cell dataKey="discount_percentage" className={styles['discount-percentage']}>
            {(rowData: IRowData) => <OfferDiscountCell rowData={rowData} campaignId={id} disabled={isReadOnly} />}
          </Cell>
        </Column>

        <Column align="left" width={72}>
          <HeaderCell className={styles['header-cell']}>
            <div>
              <p>Margem</p>
              <p>Oferta</p>
            </div>
          </HeaderCell>
          <Cell dataKey="margin" className={styles['margin']}>
            {(rowData: IRowData) => <OfferMarginCell rowData={rowData} />}
          </Cell>
        </Column>

        <Column align="left" width={72}>
          <HeaderCell className={styles['header-cell']}>
            <div>
              <p>CPI</p>
              <p>oferta</p>
            </div>
          </HeaderCell>
          <Cell dataKey="competitiveness" className={styles['cpi']}>
            {(rowData: IRowData) => <OfferCpiCell rowData={rowData} />}
          </Cell>
        </Column>

        <Column align="right" fixed="right" width={68}>
          <HeaderCell />
          <Cell dataKey="actions" className={styles['actions']}>
            {(rowData: IRowData) => (
              <div className={styles.actions}>
                <Button onClick={() => onEdit(rowData)} disabled={isReadOnly}>
                  <MdEdit />
                </Button>
                <Button onClick={() => onDelete(rowData.product_uuid)} disabled={isReadOnly}>
                  <MdDeleteOutline title="Deletar" size="md" />
                </Button>
              </div>
            )}
          </Cell>
        </Column>
      </Table>
      <Pagination
        lengthMenu={[
          {
            value: 20,
            label: 20,
          },
          {
            value: 40,
            label: 40,
          },
        ]}
        activePage={(offers?.number ?? 0) + 1}
        displayLength={offers?.size ?? 0}
        total={offers?.totalElements ?? 0}
        onChangePage={value => setPage(value - 1)}
        onChangeLength={value => setSize(value)}
      />
    </section>
  );
};
